// aiPhoneAgent.ts

import openai from "./openaiHelper";
import { z } from "zod";
import { zodResponseFormat } from "openai/helpers/zod";
import { ChatCompletionMessageParam } from "openai/resources";
import api from "./api";

export async function generateMainSystemAndStrategyPrompt(
  callDescription: string,
  callDurationMinutes: number,
  strategyType: string
): Promise<{ mainSystemPrompt: string; strategyPrompt: string }> {
  // Define the schema using Zod
  const ResponseSchema = z.object({
    main_system_prompt: z.string(),
    strategy_prompt: z.string(),
  });

  // Create the prompt
  const prompt = `As an expert Prompt Engineer, your task is to create a main system prompt and a strategy prompt for an AI phone agent that conducts realistic voice conversations with patients. These prompts will be used in a new approach where the main system prompt is provided once at the beginning of the conversation, and the strategy prompt is provided with every message.

  Call Details:
  - Description: ${callDescription}
  - Duration: Approximately ${callDurationMinutes} minutes
  - Type: ${strategyType}
  
  Context:
  1. The main system prompt will be provided once at the start of the conversation to establish the AI agent's core identity and capabilities.
  2. The strategy prompt will be provided with every message to guide the AI's responses for this specific call type.
  3. The AI agent should engage in a natural back-and-forth conversation, avoiding lengthy monologues.
  
  Requirements:
  1. Main System Prompt:
     - Establish the AI agent's identity as the healthcare communication assistant Ellie
     - Define core capabilities and limitations (e.g., scheduling, information gathering, no clinical advice)
     - Set ethical guidelines and privacy standards
     - Outline general conversational approach (e.g., empathetic, professional, clear)
     - Always Include:
        - "Each message should be as conscise as is expected in a phone call... no more than 1-2 sentences."
        - "NEVER provide clinical advice or make diagnoses."
        - "Always notify the user that this call is being recorded."
  
  2. Strategy Prompt:
     - Include brief guidelines for conversation flow (introduction, main content, conclusion)
     - Define a set of numbered steps for the main content the AI should follow to achieve the call's objectives
     - Provide a clear definition of when the conclusion is done and when to end the call.
     - If the type of call requires a patient's identity to be confirmed, include a step for that.
     - ALWAYS INCLUDE verbatim:
        - "When you have a clear reason to not wait for the user and end the call, append the string '__...__' to the end of the AI Phone Agent assistant's message."
        - "ALWAYS Wait for the patient's response before proceeding to the next step."
        - "ONLY return unformatted text responses and no JSON objects or other data types"
        - "Make the conversation light and human by tossing in filler words like 'um' and 'uh' where appropriate."
  
  Ensure both prompts foster a natural, empathetic, and efficient conversation flow. The AI should be able to adapt its responses based on the patient's input while staying on track with the call's objectives.
  
  Output your response as a JSON object with keys "main_system_prompt" and "strategy_prompt".`;

  try {
    const completion = await openai.beta.chat.completions.parse({
      model: "gpt-4o",
      messages: [{ role: "user", content: prompt }],
      response_format: zodResponseFormat(ResponseSchema, "response"),
    });

    const parsed = completion.choices[0].message.parsed;

    if (!parsed) {
      throw new Error("Parsed response is null");
    }

    return {
      mainSystemPrompt: parsed.main_system_prompt,
      strategyPrompt: parsed.strategy_prompt,
    };
  } catch (e: any) {
    console.error("Error generating prompts:", e.message);
    throw e;
  }
}

export const generateEdgeCases = async (
  mainSystemPrompt: string,
  strategyPrompt: string,
  onChunk?: (cases: string[]) => void
): Promise<string[]> => {
  try {
    let accumulatedResponse = "";
    const prompt = `Given the following main system prompt and strategy prompt for an AI phone agent:

      Main System Prompt:
      ${mainSystemPrompt}

      Strategy Prompt:
      ${strategyPrompt}

      Generate TEN challenging edge case scenarios that could occur during phone calls. These should test whether the prompts adequately cover unusual or difficult situations. 
      Each edge case should be separated by a semicolon (so we can edgeCases.split(';') it) and should not include numbers or bullet points.
      Make sure to include scenarios that test:
      - Patient confusion or misunderstanding
      - Technical difficulties
      - Complex emotional states
      - Boundary testing
      - Time pressure situations
      - Language or communication barriers
      - Emergency situations
      - Privacy concerns
      - Authentication challenges
      - Schedule conflicts`;

    await api.streamChatCompletion(
      [{ role: "user", content: prompt }],
      (chunk) => {
        // Add the new chunk to our accumulated response
        accumulatedResponse += chunk;

        // Process the current accumulated response into edge cases
        const currentEdgeCases = accumulatedResponse
          .split(";")
          .map((line) => line.trim())
          .filter((line) => line.length > 0)
          .map((line) => line.replace(/^[-*•]|\d+[.)] /, "").trim())
          .filter((line) => line.length > 10);

        // Update UI with current processed cases
        if (onChunk) {
          onChunk(currentEdgeCases);
        }
      }
    );

    // Process the final response
    const finalEdgeCases = accumulatedResponse
      .split(";")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => line.replace(/^[-*•]|\d+[.)] /, "").trim())
      .filter((line) => line.length > 10);

    // If we somehow got no valid cases, throw an error
    if (finalEdgeCases.length === 0) {
      throw new Error("No valid edge cases generated");
    }

    return finalEdgeCases;
  } catch (e: any) {
    console.error("Error generating edge cases:", e.message);
    throw e;
  }
};

export async function generateSimulatedPatientResponse(
  conversationHistory: ChatCompletionMessageParam[],
  edgeCase: string,
  onChunk?: (chunk: string) => void
): Promise<string> {
  try {
    let accumulatedResponse = "";
    const simulationPrompt = `You are simulating a patient in a phone conversation with an AI medical office assistant.
    
Response Format Requirements:
1. Start your response with "Patient: "
2. Keep responses focused and natural
3. If ending the conversation, add "END_CONVERSATION" on a new line
4. Maintain consistent personality based on the edge case
5. No meta-commentary or role-playing notes

Edge Case Context: ${edgeCase}

Previous Conversation:
${conversationHistory
  .filter((msg) => msg.role !== "system")
  .map((msg) => msg.content)
  .join("\n")}

Respond as the patient, following the format requirements above.`;

    await api.streamChatCompletion(
      [...conversationHistory, { role: "user", content: simulationPrompt }],
      (chunk) => {
        // Accumulate the full response
        accumulatedResponse += chunk;

        // Only update if we have a complete patient response
        if (accumulatedResponse.includes("Patient:")) {
          const cleanedResponse = accumulatedResponse.trim();
          onChunk?.(cleanedResponse);
        }
      }
    );

    // Clean up the final response
    let finalResponse = accumulatedResponse.trim();
    if (!finalResponse.startsWith("Patient:")) {
      finalResponse = `Patient: ${finalResponse}`;
    }

    return finalResponse;
  } catch (error) {
    console.error("Error generating simulated patient response:", error);
    throw error;
  }
}

export async function generateAIPhoneAgentResponse(
  conversationHistory: ChatCompletionMessageParam[],
  strategyPrompt: string,
  onChunk?: (chunk: string) => void
): Promise<string> {
  try {
    let accumulatedResponse = "";
    const agentPrompt = `You are an AI medical office assistant handling a phone call.

Response Format Requirements:
1. Start your response with "AI: "
2. Keep responses professional, clear, and concise
3. If ending the conversation, add "END_CONVERSATION" on a new line
4. Stay focused on medical office tasks
5. No meta-commentary or role-playing notes

Strategy Guidelines:
${strategyPrompt}

Previous Conversation:
${conversationHistory
  .filter((msg) => msg.role !== "system")
  .map((msg) => msg.content)
  .join("\n")}

Respond as the AI agent, following the format requirements above.`;

    await api.streamChatCompletion(
      [...conversationHistory, { role: "user", content: agentPrompt }],
      (chunk) => {
        // Accumulate the full response
        accumulatedResponse += chunk;

        // Only update if we have a complete AI response
        if (accumulatedResponse.includes("AI:")) {
          const cleanedResponse = accumulatedResponse.trim();
          onChunk?.(cleanedResponse);
        }
      }
    );

    // Clean up the final response
    let finalResponse = accumulatedResponse.trim();
    if (!finalResponse.startsWith("AI:")) {
      finalResponse = `AI: ${finalResponse}`;
    }

    return finalResponse;
  } catch (error) {
    console.error("Error generating AI phone agent response:", error);
    throw error;
  }
}

export async function initializeConversation(
  mainSystemPrompt: string,
  strategyPrompt: string,
  callDescription: string
): Promise<ChatCompletionMessageParam[]> {
  const initialMessage = {
    role: "system" as const,
    content: `${mainSystemPrompt}

Context: ${callDescription}

Format Instructions:
- AI Agent responses should be prefixed with "AI:"
- Patient responses should be prefixed with "Patient:"
- To end the conversation, include "END_CONVERSATION" on a new line
- Keep responses focused and concise
- Use clear paragraph breaks between speaker turns

Strategy Guidelines:
${strategyPrompt}`,
  };

  return [initialMessage];
}

export async function judgeConversation(
  conversation: ChatCompletionMessageParam[],
  edgeCase: string
): Promise<string> {
  const JudgmentSchema = z.object({
    judgment: z.string(),
  });

  const prompt = `Please evaluate the following conversation between an AI phone agent and a simulated patient, based on the given edge case scenario. Provide a judgment on how well the AI agent handled the situation, highlighting strengths and areas for improvement.
  
  Edge Case:
  ${edgeCase}
  
  Conversation:
  ${conversation.map((msg) => `${msg.role}: ${msg.content}`).join("\n")}
  
  Provide your judgment as a string under the key "judgment".`;

  try {
    const completion = await openai.beta.chat.completions.parse({
      model: "gpt-4o",
      messages: [{ role: "user", content: prompt }],
      response_format: zodResponseFormat(JudgmentSchema, "response"),
    });

    const parsed = completion.choices[0].message.parsed;

    if (!parsed) {
      throw new Error("Parsed response is null");
    }

    return parsed.judgment;
  } catch (e: any) {
    console.error("Error judging conversation:", e.message);
    throw e;
  }
}

export async function refinePrompts(
  mainSystemPrompt: string,
  strategyPrompt: string,
  judgments: { edgeCase: string; judgment: string }[]
): Promise<{ refinedMainSystemPrompt: string; refinedStrategyPrompt: string }> {
  const RefinedPromptsSchema = z.object({
    refined_main_system_prompt: z.string(),
    refined_strategy_prompt: z.string(),
  });

  const prompt = `Given the following main system prompt and strategy prompt for an AI phone agent, along with judgments on simulated conversations for various edge cases, please refine both prompts to improve the AI agent's performance. Keep in mind that the main system prompt is provided once at the start of the conversation, while the strategy prompt is provided with every message.
  
  Current Main System Prompt:
  ${mainSystemPrompt}
  
  Current Strategy Prompt:
  ${strategyPrompt}
  
  Judgments:
  ${judgments
    .map((j) => `Edge Case: ${j.edgeCase}\nJudgment: ${j.judgment}\n`)
    .join("\n")}
  
  Please provide refined versions of both prompts that address the issues identified in the judgments and improve the AI agent's ability to handle these edge cases. Remember:
  1. The main system prompt should be concise and focus on establishing the AI's core identity and capabilities.
  2. The strategy prompt should be more focused and provide specific guidance for each response.

  Provide the output as JSON with the keys "refined_main_system_prompt" and "refined_strategy_prompt".`;

  try {
    const completion = await openai.beta.chat.completions.parse({
      model: "gpt-4o",
      messages: [{ role: "user", content: prompt }],
      response_format: zodResponseFormat(RefinedPromptsSchema, "response"),
    });

    const parsed = completion.choices[0].message.parsed;

    if (!parsed) {
      throw new Error("Parsed response is null");
    }

    return {
      refinedMainSystemPrompt: parsed.refined_main_system_prompt,
      refinedStrategyPrompt: parsed.refined_strategy_prompt,
    };
  } catch (e: any) {
    console.error("Error refining prompts:", e.message);
    throw e;
  }
}
